/**
 * Utility functions to track which conversations have been viewed by the user
 * Stores data in localStorage: messageViewedConversations_{userId} = { conversationId: { lastViewedAt, unreadCountWhenViewed } }
 * 
 * Also provides a simple event system to notify React components when viewed status changes
 */

const getStorageKey = (userId) => {
  return userId ? `messageViewedConversations_${userId}` : 'messageViewedConversations';
};

// Simple event emitter for notifying React components
let listeners = new Set();

const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

/**
 * Subscribe to viewed conversation changes
 * @param {Function} callback - Function to call when viewed status changes
 * @returns {Function} Unsubscribe function
 */
export const subscribeToViewedChanges = (callback) => {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
};

/**
 * Get all viewed conversations from localStorage for a specific user
 * @param {string} userId - The user ID (optional, for scoping)
 * @returns {Object} Map of conversationId -> { lastViewedAt, unreadCountWhenViewed }
 */
export const getViewedConversations = (userId = null) => {
  try {
    const storageKey = getStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[messageViewedTracker] Error reading from localStorage:', error);
    return {};
  }
};

/**
 * Get the last viewed info for a specific conversation
 * @param {string} conversationId - The conversation ID
 * @param {string} userId - The user ID (optional, for scoping)
 * @returns {Object|null} { lastViewedAt, unreadCountWhenViewed } or null if not viewed
 */
export const getLastViewedConversation = (conversationId, userId = null) => {
  if (!conversationId) return null;
  const viewedConversations = getViewedConversations(userId);
  return viewedConversations[conversationId] || null;
};

/**
 * Mark a conversation as viewed
 * @param {string} conversationId - The conversation ID
 * @param {number} unreadCount - The current unread count (will be stored as 0 after viewing)
 * @param {string} userId - The user ID (optional, for scoping)
 */
export const markConversationAsViewed = (conversationId, unreadCount = 0, userId = null) => {
  if (!conversationId) return;
  
  try {
    const storageKey = getStorageKey(userId);
    const viewedConversations = getViewedConversations(userId);
    viewedConversations[conversationId] = {
      lastViewedAt: new Date().toISOString(),
      unreadCountWhenViewed: 0 // After viewing, unread count is 0
    };
    localStorage.setItem(storageKey, JSON.stringify(viewedConversations));
    
    // Notify listeners that viewed status changed
    notifyListeners();
  } catch (error) {
    console.error('[messageViewedTracker] Error saving to localStorage:', error);
  }
};

/**
 * Check if a conversation has unviewed messages
 * @param {Object} conversation - The conversation object with id, hostUnreadCount, guestUnreadCount, hostID, guestID
 * @param {string} userId - The user ID (optional, for scoping)
 * @returns {boolean} True if conversation has unread messages that haven't been viewed
 */
export const hasUnviewedMessages = (conversation, userId = null) => {
  if (!conversation?.id || !userId) return false;
  
  // Calculate current unread count for this user
  const isHost = conversation.hostID === userId;
  const currentUnreadCount = isHost 
    ? (conversation.hostUnreadCount || 0)
    : (conversation.guestUnreadCount || 0);
  
  // If no unread messages, no notification needed
  if (currentUnreadCount <= 0) return false;
  
  // Check if conversation was viewed after messages were read
  const lastViewed = getLastViewedConversation(conversation.id, userId);
  
  // If never viewed and has unread messages, show notification
  if (!lastViewed) {
    return currentUnreadCount > 0;
  }
  
  // If viewed but unread count increased since last view, show notification
  // This handles the case where new messages arrived after viewing
  return currentUnreadCount > (lastViewed.unreadCountWhenViewed || 0);
};

/**
 * Clear viewed status for a conversation (useful for testing or reset)
 * @param {string} conversationId - The conversation ID
 * @param {string} userId - The user ID (optional, for scoping)
 */
export const clearViewedConversation = (conversationId, userId = null) => {
  if (!conversationId) return;
  
  try {
    const storageKey = getStorageKey(userId);
    const viewedConversations = getViewedConversations(userId);
    delete viewedConversations[conversationId];
    localStorage.setItem(storageKey, JSON.stringify(viewedConversations));
    notifyListeners();
  } catch (error) {
    console.error('[messageViewedTracker] Error clearing viewed conversation:', error);
  }
};

/**
 * Clear all viewed conversations for a user (useful for testing or logout)
 * @param {string} userId - The user ID (optional, clears all if not provided)
 */
export const clearAllViewedConversations = (userId = null) => {
  try {
    const storageKey = getStorageKey(userId);
    localStorage.removeItem(storageKey);
    notifyListeners();
  } catch (error) {
    console.error('[messageViewedTracker] Error clearing all viewed conversations:', error);
  }
};
